// Admin Risks Tab — Risk analysis: matrix + list view
// Likelihood × Impact heatmap, with category, status, mitigation
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

const CAT_LABELS_FR = {
  mecenat: "Mécénat", fresques: "Fresques", logistique: "Logistique",
  communication: "Communication", partenariats: "Partenariats", autre: "Autre",
};
const LIKELIHOOD_LABELS_FR = {
  unlikely: "Improbable", possible: "Possible", likely: "Probable", very_likely: "Très probable",
};
const IMPACT_LABELS_FR = {
  low: "Faible", medium: "Modéré", high: "Élevé", critical: "Critique",
};
const STATUS_LABELS_FR = {
  open: "Ouvert", mitigated: "Atténué", closed: "Clos",
};
const STATUS_COLORS = {
  open: { bg: "rgba(153,27,27,0.12)", fg: "var(--cinnabar)" },
  mitigated: { bg: "rgba(212,175,55,0.18)", fg: "var(--gold)" },
  closed: { bg: "rgba(83,128,72,0.12)", fg: "var(--moss)" },
};

// Risk score = likelihood index × impact index. Returns color/label.
const LIKELIHOOD_ORDER = ["unlikely", "possible", "likely", "very_likely"];
const IMPACT_ORDER = ["low", "medium", "high", "critical"];

function riskCellColor(li, ii) {
  // li, ii in 0..3 — score 0..9
  const score = (li + 1) * (ii + 1);
  if (score >= 12) return { bg: "rgba(153,27,27,0.85)", fg: "#fff", label: "Critique" };
  if (score >= 8) return { bg: "rgba(212,107,55,0.7)", fg: "#fff", label: "Élevé" };
  if (score >= 4) return { bg: "rgba(212,175,55,0.55)", fg: "var(--ink)", label: "Modéré" };
  return { bg: "rgba(83,128,72,0.4)", fg: "var(--ink)", label: "Faible" };
}

const emptyRisk = () => ({
  title: "", description: "", category: "autre",
  likelihood: "possible", impact: "medium",
  mitigation: "", status: "open",
});

export default function RisksTab({ locale }) {
  const isFr = locale === "fr";
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/risks");
      setRisks(data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const removeRisk = async (r) => {
    if (!window.confirm(isFr ? `Supprimer le risque « ${r.title} » ?` : `Delete risk "${r.title}"?`)) return;
    await api.delete(`/admin/risks/${r.id}`);
    load();
  };

  // Build a 4x4 matrix: rows = likelihood (top=very_likely), cols = impact (left=low)
  const matrix = LIKELIHOOD_ORDER.slice().reverse().map((lk) =>
    IMPACT_ORDER.map((im) =>
      risks.filter((r) => r.likelihood === lk && r.impact === im && r.status !== "closed")
    )
  );

  const filtered = filterStatus === "all" ? risks : risks.filter((r) => r.status === filterStatus);
  const counts = {
    open: risks.filter((r) => r.status === "open").length,
    mitigated: risks.filter((r) => r.status === "mitigated").length,
    closed: risks.filter((r) => r.status === "closed").length,
  };

  return (
    <div data-testid="risks-tab" className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl">{isFr ? "Analyse des risques" : "Risk analysis"}</h2>
          <p className="font-body text-sm" style={{ color: "var(--muted)" }}>
            {isFr ? "Matrice probabilité × impact + mitigation." : "Likelihood × impact matrix + mitigation."}
          </p>
        </div>
        <button className="btn btn-primary" data-testid="risk-new"
                onClick={() => setEditing(emptyRisk())}>
          + {isFr ? "Nouveau risque" : "New risk"}
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <div className="admin-card text-center">
          <div className="label-caps mb-1" style={{ color: "var(--cinnabar)" }}>{isFr ? "Ouverts" : "Open"}</div>
          <div className="font-display text-3xl">{counts.open}</div>
        </div>
        <div className="admin-card text-center">
          <div className="label-caps mb-1" style={{ color: "var(--gold)" }}>{isFr ? "Atténués" : "Mitigated"}</div>
          <div className="font-display text-3xl">{counts.mitigated}</div>
        </div>
        <div className="admin-card text-center">
          <div className="label-caps mb-1" style={{ color: "var(--moss)" }}>{isFr ? "Clos" : "Closed"}</div>
          <div className="font-display text-3xl">{counts.closed}</div>
        </div>
      </div>

      {editing && (
        <RiskEditor isFr={isFr} initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}

      {loading && <div className="font-display italic" style={{ color: "var(--muted)" }}>…</div>}

      {/* Matrix */}
      {!loading && (
        <div data-testid="risks-matrix">
          <div className="deco-rule mb-4">
            <span className="font-caps text-[0.78rem]">
              {isFr ? "Matrice (risques ouverts & atténués)" : "Matrix (open & mitigated)"}
            </span>
          </div>
          <div className="bg-white border p-4 overflow-x-auto" style={{ borderColor: "var(--border)" }}>
            <table className="w-full border-collapse" style={{ minWidth: 600 }}>
              <thead>
                <tr>
                  <th></th>
                  {IMPACT_ORDER.map((im) => (
                    <th key={im} className="font-caps text-[0.6rem] p-2"
                        style={{ letterSpacing: "0.15em", color: "var(--muted)" }}>
                      {IMPACT_LABELS_FR[im]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {LIKELIHOOD_ORDER.slice().reverse().map((lk, li) => {
                  // li is reversed index (0 = very_likely top)
                  const realLi = 3 - li;
                  return (
                    <tr key={lk}>
                      <td className="font-caps text-[0.6rem] p-2 text-right"
                          style={{ letterSpacing: "0.15em", color: "var(--muted)", whiteSpace: "nowrap" }}>
                        {LIKELIHOOD_LABELS_FR[lk]}
                      </td>
                      {IMPACT_ORDER.map((im, ii) => {
                        const cell = matrix[li][ii];
                        const col = riskCellColor(realLi, ii);
                        return (
                          <td key={im} className="border p-2 align-top" style={{
                            borderColor: "var(--border)", background: col.bg, color: col.fg,
                            minWidth: 110, minHeight: 70,
                          }}>
                            {cell.length === 0 ? (
                              <span className="font-caps text-[0.55rem] opacity-50">{col.label}</span>
                            ) : (
                              <div className="space-y-1">
                                {cell.map((r) => (
                                  <button key={r.id}
                                    data-testid={`matrix-risk-${r.id}`}
                                    onClick={() => setEditing({ ...r })}
                                    className="block w-full text-left text-xs underline-offset-2 hover:underline"
                                    style={{ color: col.fg }}>
                                    {r.title}
                                  </button>
                                ))}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="font-caps text-[0.55rem] mt-3 text-center" style={{ color: "var(--muted)", letterSpacing: "0.15em" }}>
              ← {isFr ? "IMPACT" : "IMPACT"} →
            </div>
          </div>
        </div>
      )}

      {/* List with filter */}
      {!loading && (
        <div data-testid="risks-list">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="deco-rule">
              <span className="font-caps text-[0.78rem]">
                {isFr ? "Liste des risques" : "Risk list"} <span style={{ color: "var(--muted)" }}>· {filtered.length}</span>
              </span>
            </div>
            <select className="input-classic" style={{ width: "auto", background: "transparent" }}
                    value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                    data-testid="risk-filter-status">
              <option value="all">{isFr ? "Tous statuts" : "All statuses"}</option>
              <option value="open">{STATUS_LABELS_FR.open}</option>
              <option value="mitigated">{STATUS_LABELS_FR.mitigated}</option>
              <option value="closed">{STATUS_LABELS_FR.closed}</option>
            </select>
          </div>
          {filtered.length === 0 ? (
            <div className="font-body text-sm italic" style={{ color: "var(--muted)" }}>
              {isFr ? "Aucun risque." : "No risk."}
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((r) => {
                const sc = STATUS_COLORS[r.status] || STATUS_COLORS.open;
                return (
                  <div key={r.id} data-testid={`risk-${r.id}`} className="bg-white border p-4"
                       style={{ borderColor: "var(--border)" }}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-caps text-[0.6rem] px-2 py-0.5 border"
                                style={{ borderColor: sc.fg, color: sc.fg, background: sc.bg, letterSpacing: "0.18em" }}>
                            {STATUS_LABELS_FR[r.status]}
                          </span>
                          <span className="font-caps text-[0.55rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
                            · {CAT_LABELS_FR[r.category] || r.category}
                          </span>
                          <span className="font-caps text-[0.55rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
                            · {LIKELIHOOD_LABELS_FR[r.likelihood]} / {IMPACT_LABELS_FR[r.impact]}
                          </span>
                        </div>
                        <div className="font-display text-lg">{r.title}</div>
                        {r.description && (
                          <p className="font-body text-sm mt-1" style={{ color: "var(--ink-2)" }}>{r.description}</p>
                        )}
                        {r.mitigation && (
                          <div className="mt-2 pl-3 border-l-2" style={{ borderColor: "var(--gold)" }}>
                            <div className="font-caps text-[0.55rem]" style={{ color: "var(--gold)", letterSpacing: "0.18em" }}>
                              {isFr ? "Mitigation" : "Mitigation"}
                            </div>
                            <p className="font-body text-sm">{r.mitigation}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button className="btn btn-outline btn-xs" data-testid={`risk-edit-${r.id}`}
                                onClick={() => setEditing({ ...r })}>
                          {isFr ? "Modifier" : "Edit"}
                        </button>
                        <button className="btn btn-outline btn-xs" style={{ color: "var(--cinnabar)" }}
                                data-testid={`risk-delete-${r.id}`}
                                onClick={() => removeRisk(r)}>
                          {isFr ? "Supprimer" : "Delete"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RiskEditor({ initial, isFr, onCancel, onSaved }) {
  const [r, setR] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setR((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setErr("");
    if (!r.title.trim()) { setErr(isFr ? "Titre requis." : "Title required."); return; }
    setBusy(true);
    try {
      const payload = {
        title: r.title, description: r.description, category: r.category,
        likelihood: r.likelihood, impact: r.impact,
        mitigation: r.mitigation, status: r.status,
      };
      if (r.id) await api.patch(`/admin/risks/${r.id}`, payload);
      else await api.post("/admin/risks", payload);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };
  return (
    <div className="admin-card space-y-4" data-testid="risk-editor">
      <h3 className="font-display text-xl">
        {r.id ? (isFr ? "Modifier le risque" : "Edit risk") : (isFr ? "Nouveau risque" : "New risk")}
      </h3>
      <label className="block">
        <span className="label-caps">{isFr ? "Titre *" : "Title *"}</span>
        <input className="input-classic mt-1" value={r.title} onChange={(e) => set("title", e.target.value)} data-testid="risk-title" />
      </label>
      <label className="block">
        <span className="label-caps">{isFr ? "Description" : "Description"}</span>
        <textarea rows={2} className="input-classic mt-1" value={r.description} onChange={(e) => set("description", e.target.value)} data-testid="risk-desc" />
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="label-caps">{isFr ? "Catégorie" : "Category"}</span>
          <select className="input-classic mt-1" value={r.category} onChange={(e) => set("category", e.target.value)} data-testid="risk-cat">
            {Object.entries(CAT_LABELS_FR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Statut" : "Status"}</span>
          <select className="input-classic mt-1" value={r.status} onChange={(e) => set("status", e.target.value)} data-testid="risk-status">
            {Object.entries(STATUS_LABELS_FR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Probabilité" : "Likelihood"}</span>
          <select className="input-classic mt-1" value={r.likelihood} onChange={(e) => set("likelihood", e.target.value)} data-testid="risk-likelihood">
            {LIKELIHOOD_ORDER.map((k) => <option key={k} value={k}>{LIKELIHOOD_LABELS_FR[k]}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Impact" : "Impact"}</span>
          <select className="input-classic mt-1" value={r.impact} onChange={(e) => set("impact", e.target.value)} data-testid="risk-impact">
            {IMPACT_ORDER.map((k) => <option key={k} value={k}>{IMPACT_LABELS_FR[k]}</option>)}
          </select>
        </label>
      </div>
      <label className="block">
        <span className="label-caps">{isFr ? "Plan de mitigation" : "Mitigation plan"}</span>
        <textarea rows={3} className="input-classic mt-1" value={r.mitigation} onChange={(e) => set("mitigation", e.target.value)} data-testid="risk-mitigation"
                  placeholder={isFr ? "Mesures prévues pour réduire ce risque…" : "Planned mitigation measures…"} />
      </label>
      {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}
      <div className="flex gap-3 justify-end">
        <button className="btn btn-outline" onClick={onCancel} data-testid="risk-cancel">{isFr ? "Annuler" : "Cancel"}</button>
        <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="risk-save">
          {busy ? "…" : (isFr ? "Enregistrer" : "Save")}
        </button>
      </div>
    </div>
  );
}
